import React, { useCallback, useMemo, useState } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { AutoAwesome, CloudUpload } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import PaperContainer from '@fd/ui/Layout/PaperContainer';

import { FIVE_MINUTES } from '../../../../../../../helpers/timeConstants';
import { notifyError, NotifyProps, notifySaved } from '../../../../../../../layouts/Notify/actions';
import { WhiteLabelPlayStoreConfig, whitelabelService } from '../../../../whitelabel.service';

type Props = MappedState & MappedDispatch;

export type GooglePlayFormValues = {
  BundleId: string;
  Description: string;
  ShortDescription: string;
  Copyright: string;
  Keywords: string;
  MarketingUrl: string;
  PrivacyUrl: string;
  SupportUrl: string;
  PlayConsoleAccount: string;
  ReleaseNotes: string;
  StoreIcon: File | null;
  ReadyToPublishAndroid: boolean;
};

const GooglePlayConfigurationTab = ({
  translate,
  dispatchNotifyError,
  dispatchNotifySaved,
}: Props) => {
  const currentApp = useAppSelector((state) => state.currentApp);
  const queryClient = useQueryClient();
  const appId = currentApp?.AppId || '';

  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [storeIconPreview, setStoreIconPreview] = useState<string | null>(null);

  // Query for Google Play configuration
  const {
    data: playStoreConfig,
    isLoading,
    error: configError,
  } = useQuery({
    queryKey: ['playStoreConfig', appId],
    queryFn: () => whitelabelService.getWhiteLabelPlayStoreConfig(appId),
    enabled: !!appId,
    staleTime: FIVE_MINUTES,
  });

  // Mutation for updating Google Play configuration
  const { mutate: updatePlayStoreConfig, isPending: isUpdating } = useMutation({
    mutationFn: (config: WhiteLabelPlayStoreConfig) =>
      whitelabelService.updateWhiteLabelPlayStoreConfig(appId, config),
    onSuccess: () => {
      dispatchNotifySaved();
      queryClient.invalidateQueries({
        queryKey: ['playStoreConfig', appId],
      });
    },
    onError: (error: any) => {
      dispatchNotifyError({
        message: error.message || 'Failed to save Google Play configuration',
        translate: true,
      });
    },
  });

  // Create initial values from fetched data or defaults
  const initialValues = useMemo(() => {
    return {
      BundleId: playStoreConfig?.BundleId || '',
      Description: playStoreConfig?.Description || '',
      ShortDescription: playStoreConfig?.ShortDescription || '',
      Copyright: playStoreConfig?.Copyright || '',
      Keywords: playStoreConfig?.Keywords,
      MarketingUrl: playStoreConfig?.MarketingUrl || '',
      PrivacyUrl: playStoreConfig?.PrivacyUrl || '',
      SupportUrl: playStoreConfig?.SupportUrl || '',
      PlayConsoleAccount: playStoreConfig?.PlayConsoleAccount || '',
      ReleaseNotes: playStoreConfig?.ReleaseNotes || '',
      StoreIcon: null,
      ReadyToPublishAndroid: playStoreConfig?.ReadyToPublishAndroid || false,
    };
  }, [playStoreConfig]);

  // Initialize keywords from config when available
  React.useEffect(() => {
    if (playStoreConfig?.Keywords?.length) {
      setKeywords(playStoreConfig.Keywords.split(','));
    }
  }, [playStoreConfig?.Keywords]);

  const validateUrl = (value: string) => {
    if (value && !/^https?:\/\/.+/.test(value)) {
      return translate('Must_be_a_valid_URL') as string;
    }
    return null;
  };

  const handleKeywordAdd = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      const newKeywords = [...keywords, keywordInput.trim()];
      setKeywords(newKeywords);
      setKeywordInput('');
      return newKeywords.join(',');
    }
    return keywords.join(',');
  };

  const handleKeywordRemove = (keywordToRemove: string) => {
    const newKeywords = keywords.filter((keyword) => keyword !== keywordToRemove);
    setKeywords(newKeywords);
    return newKeywords.join(',');
  };

  const handleKeywordKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleKeywordAdd();
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate image dimensions (1024x1024)
      const img = new Image();
      img.onload = () => {
        if (img.width === 1024 && img.height === 1024) {
          setStoreIconPreview(URL.createObjectURL(file));
        } else {
          dispatchNotifyError({
            message: 'Store_icon_must_be_1024x1024_pixels',
            translate: true,
          });
        }
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const handleSubmit = useCallback(
    async (values: GooglePlayFormValues) => {
      const configData = {
        ...values,
        Keywords: keywords.join(','), // Use the keywords state
      };
      updatePlayStoreConfig(configData);
    },
    [updatePlayStoreConfig, keywords]
  );

  if (isLoading) {
    return (
      <PaperContainer fluid>
        <Grid container spacing={3} sx={{ padding: '24px' }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              <Translate id="Google_Play_Configuration" />
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Loading Google Play configuration...
            </Typography>
          </Grid>
        </Grid>
      </PaperContainer>
    );
  }

  return (
    <PaperContainer fluid>
      <Grid container spacing={3} sx={{ padding: '24px' }}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            <Translate id="Google_Play_Configuration" />
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <Translate id="Configure_google_play_store_settings_and_deployment" />
          </Typography>
        </Grid>

        {/* Config Error Message */}
        {configError && (
          <Grid item xs={12}>
            <Typography variant="body2" color="error">
              Failed to load Google Play configuration. Please try again.
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={handleSubmit}
            validate={(values) => {
              const errors: Partial<GooglePlayFormValues> = {};

              // Validate short description length
              if (values.ShortDescription && values.ShortDescription.length > 120) {
                errors.ShortDescription = translate(
                  'Short_description_max_120_characters'
                ) as string;
              }

              // Validate URL formats
              const marketingUrlError = validateUrl(values.MarketingUrl);
              if (marketingUrlError) {
                errors.MarketingUrl = marketingUrlError;
              }

              const privacyUrlError = validateUrl(values.PrivacyUrl);
              if (privacyUrlError) {
                errors.PrivacyUrl = privacyUrlError;
              }

              const supportUrlError = validateUrl(values.SupportUrl);
              if (supportUrlError) {
                errors.SupportUrl = supportUrlError;
              }

              return Object.keys(errors).length > 0 ? errors : undefined;
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, dirty }) => (
              <Form>
                {errors && (
                  <Grid container spacing={3} sx={{ marginBottom: 2 }}>
                    {Object.entries(errors).map(([key, value]) => (
                      <Grid item xs={12} sx={{ marginBottom: 1 }} key={key}>
                        <Typography variant="body2" color="error">
                          {typeof value === 'string'
                            ? value
                            : value?.toString?.() || `Error at ${key}`}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                )}
                <Grid container spacing={3}>
                  {/* Bundle ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="BundleId"
                      label={translate('Bundle_Id')}
                      value={values.BundleId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.BundleId && errors.BundleId)}
                      helperText={touched.BundleId && errors.BundleId?.toString()}
                      fullWidth
                    />
                  </Grid>

                  {/* Play Console Account */}
                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={!!(touched.PlayConsoleAccount && errors.PlayConsoleAccount)}
                    >
                      <InputLabel>{translate('Play_Console_Account')}</InputLabel>
                      <Select
                        name="PlayConsoleAccount"
                        value={values.PlayConsoleAccount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label={translate('Play_Console_Account')}
                      >
                        <MenuItem value="IE">IE</MenuItem>
                        <MenuItem value="UK">UK</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Short Description */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="ShortDescription"
                      label={translate('Short_Description')}
                      value={values.ShortDescription}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.ShortDescription && errors.ShortDescription)}
                      helperText={touched.ShortDescription && errors.ShortDescription?.toString()}
                      fullWidth
                      inputProps={{ maxLength: 120 }}
                    />
                  </Grid>

                  {/* Copyright */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      name="Copyright"
                      label={translate('Copyright')}
                      value={values.Copyright}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.Copyright && errors.Copyright)}
                      helperText={touched.Copyright && errors.Copyright?.toString()}
                      fullWidth
                    />
                  </Grid>

                  {/* Keywords */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Translate id="Keywords" />
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {keywords.map((keyword, index) => (
                        <Chip
                          key={index}
                          label={keyword}
                          onDelete={() => {
                            const newKeywords = handleKeywordRemove(keyword);
                            handleChange({ target: { name: 'Keywords', value: newKeywords } });
                          }}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <input type="hidden" name="Keywords" value={values.Keywords} />
                    <TextField
                      value={keywordInput}
                      onChange={(e) => {
                        setKeywordInput(e.target.value);
                      }}
                      onKeyPress={handleKeywordKeyPress}
                      placeholder={translate('Add_keyword_and_press_enter_or_comma') as string}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <Button
                            onClick={() => {
                              const newKeywords = handleKeywordAdd();
                              handleChange({ target: { name: 'Keywords', value: newKeywords } });
                            }}
                            disabled={!keywordInput.trim()}
                            size="small"
                          >
                            <Translate id="Add" />
                          </Button>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Marketing URL */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="MarketingUrl"
                      label={translate('Marketing_URL')}
                      value={values.MarketingUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.MarketingUrl && errors.MarketingUrl)}
                      helperText={touched.MarketingUrl && errors.MarketingUrl?.toString()}
                      fullWidth
                      placeholder="https://example.com"
                    />
                  </Grid>

                  {/* Privacy URL */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="PrivacyUrl"
                      label={translate('Privacy_URL')}
                      value={values.PrivacyUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.PrivacyUrl && errors.PrivacyUrl)}
                      helperText={touched.PrivacyUrl && errors.PrivacyUrl?.toString()}
                      fullWidth
                      placeholder="https://example.com/privacy"
                    />
                  </Grid>

                  {/* Support URL */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="SupportUrl"
                      label={translate('Support_URL')}
                      value={values.SupportUrl}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.SupportUrl && errors.SupportUrl)}
                      helperText={touched.SupportUrl && errors.SupportUrl?.toString()}
                      fullWidth
                      placeholder="https://example.com/support"
                    />
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Translate id="Description" />
                    </Typography>
                    <TextField
                      name="Description"
                      value={values.Description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.Description && errors.Description)}
                      helperText={touched.Description && errors.Description?.toString()}
                      fullWidth
                      multiline
                      rows={4}
                      placeholder={translate('Enter_app_description_with_emojis_allowed') as string}
                      InputProps={{
                        endAdornment: (
                          <IconButton disabled size="small" sx={{ mr: 1 }}>
                            <AutoAwesome />
                          </IconButton>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Release Notes */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Translate id="Release_Notes" />
                    </Typography>
                    <TextField
                      name="ReleaseNotes"
                      value={values.ReleaseNotes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!(touched.ReleaseNotes && errors.ReleaseNotes)}
                      helperText={touched.ReleaseNotes && errors.ReleaseNotes?.toString()}
                      fullWidth
                      multiline
                      rows={4}
                      placeholder={translate('Enter_release_notes') as string}
                    />
                  </Grid>

                  {/* Store Icon */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Translate id="Store_Icon" />
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        border: '2px dashed #ccc',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      {storeIconPreview ? (
                        <Box>
                          <img
                            src={storeIconPreview}
                            alt="Store Icon Preview"
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                          />
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            {translate('Store_icon_preview_1024x1024')}
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            <Translate id="Drag_and_drop_store_icon_or_click_to_upload" />
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <Translate id="Store_icon_must_be_1024x1024_pixels" />
                          </Typography>
                        </Box>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        id="store-icon-upload"
                      />
                      <label htmlFor="store-icon-upload">
                        <Button
                          component="span"
                          variant="outlined"
                          startIcon={<CloudUpload />}
                          sx={{ mt: 2 }}
                        >
                          <Translate id="Upload_Store_Icon" />
                        </Button>
                      </label>
                    </Paper>
                  </Grid>

                  {/* Ready to Publish Android Switch */}
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <Switch
                          name="ReadyToPublishAndroid"
                          checked={values.ReadyToPublishAndroid}
                          onChange={handleChange}
                          color="primary"
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">
                          <Translate id="Ready_to_publish_Android" />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isUpdating || !dirty}
                      sx={{ mt: 2 }}
                    >
                      {isUpdating ? (
                        <Translate id="Saving" />
                      ) : (
                        <Translate id="Save_Configuration" />
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </PaperContainer>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state),
});

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  dispatchNotifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  dispatchNotifySaved: () => dispatch(notifySaved()),
});

export default connect(mapStateToProps, mapDispatchToProps)(GooglePlayConfigurationTab);
